import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { School, Building2, PartyPopper, Users, HeartHandshake, Sparkles, ArrowRight, Mail, Phone, CheckCircle2, ShieldCheck } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { RelatedLinks } from "@/components/RelatedLinks";
import { usePageMeta } from "@/hooks/usePageMeta";
import { SITE } from "@/data/site";

const AUDIENCES = [
  { icon: School, title: "Schools", desc: "Curriculum workshops, assemblies and after-school clubs." },
  { icon: Building2, title: "Organisations", desc: "Corporate sessions, away days and wellbeing programmes." },
  { icon: PartyPopper, title: "Festivals", desc: "Performance bookings, stage takeovers and audience workshops." },
  { icon: Users, title: "Youth Programmes", desc: "Borough councils, charities and outreach initiatives." },
  { icon: HeartHandshake, title: "Team Building", desc: "Energetic, choreography-led group experiences." },
  { icon: Sparkles, title: "Bespoke Workshops", desc: "Custom sessions designed around your brief and audience." },
];

const PROCESS = [
  { step: "01", title: "Enquire", desc: "Send us your brief — audience, dates, location." },
  { step: "02", title: "Tailor", desc: "We design the workshop around your goals." },
  { step: "03", title: "Deliver", desc: "Our team turns up ready to move your people." },
];

const PACKAGES = [
  { title: "One-off Masterclass", desc: "A single workshop block delivered at your venue." , price: "From £350" },
  { title: "Multi-week Residency", desc: "Recurring sessions over weeks or terms with progression.", price: "Quoted" },
  { title: "Festival / Event Booking", desc: "Performance + audience activation packages.", price: "Quoted" },
];

export default function SchoolsOrganisationsPage() {
  usePageMeta(
    "Schools & Organisations · Bespoke USDD Workshops",
    "Book USDD Academy for schools, festivals, councils, organisations and team-building. Bespoke street-dance workshops, residencies and performance packages across the UK."
  );
  return (
    <>
      <Header />
      <main>
        <PageHero
          overline="06 · Schools & Organisations"
          title="Book USDD for"
          highlight="your team."
          description="From one-off masterclasses to multi-week residencies — we design and deliver bespoke training for schools, councils, festivals, organisations and brands across the UK."
          ctaPrimary={{ label: "Request a Quote", to: "/contact" }}
          ctaSecondary={{ label: "Email us", href: `mailto:${SITE.contact.email}?subject=USDD%20Workshop%20Booking%20Enquiry` }}
          bg={SITE.classImg6}
        />

        {/* Audiences */}
        <section className="py-20 sm:py-24 bg-black">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 pb-8 border-b border-white/10">
              <div className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-4">
                  <span className="h-px w-10 bg-brand-yellow" />
                  <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">Who we work with</span>
                </div>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-wide leading-[0.95]">
                  One brief. <br /> One team. <span className="text-brand-yellow text-glow-yellow">One unforgettable</span> session.
                </h2>
              </div>
              <div className="lg:col-span-5 lg:pt-6">
                <p className="text-zinc-300/90 text-base sm:text-lg leading-relaxed">
                  USDD has delivered programmes for boroughs, schools, charities,
                  brands and festivals. Every booking is bespoke — built around
                  your audience, age range, dates and goals.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/contact"
                    data-testid="schools-enquire-btn"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-xs tracking-[0.2em] hover:bg-brand-yellowHover hover:scale-[1.03] transition-all glow-yellow"
                  >
                    Enquire now <ArrowRight size={14} />
                  </Link>
                  <a
                    href={`mailto:${SITE.contact.email}?subject=USDD%20Workshop%20Booking%20Enquiry`}
                    data-testid="schools-email-btn"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/20 bg-white/5 backdrop-blur text-white font-bold uppercase text-xs tracking-[0.2em] hover:bg-white hover:text-black transition-all"
                  >
                    <Mail size={14} /> Email
                  </a>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {AUDIENCES.map((a, i) => (
                <motion.div
                  key={a.title}
                  data-testid={`schools-audience-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.06 }}
                  className="group relative p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-brand-yellow/50 hover:bg-white/[0.06] hover:-translate-y-1 transition-all"
                >
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-11 h-11 rounded-xl bg-brand-yellow/10 border border-brand-yellow/30 flex items-center justify-center group-hover:bg-brand-yellow transition-all">
                      <a.icon size={20} className="text-brand-yellow group-hover:text-black transition-colors" />
                    </div>
                  </div>
                  <h3 className="font-display text-2xl text-white tracking-wide leading-tight">{a.title}</h3>
                  <p className="mt-2 text-xs text-zinc-400 leading-relaxed">{a.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-20 bg-gradient-to-b from-black via-zinc-950 to-black">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-10 bg-brand-yellow" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">How it works</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 rounded-3xl overflow-hidden border border-white/10">
              {PROCESS.map((p) => (
                <div key={p.step} className="bg-black/80 p-7">
                  <div className="flex items-baseline gap-4">
                    <span className="font-display text-5xl text-brand-yellow tracking-wide">{p.step}</span>
                    <div>
                      <div className="font-display text-2xl text-white tracking-wide">{p.title}</div>
                      <div className="text-xs text-zinc-400 mt-1 max-w-xs">{p.desc}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Packages */}
        <section className="py-20 bg-black">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-10 bg-brand-yellow" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">Indicative packages</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {PACKAGES.map((p, i) => (
                <div
                  key={p.title}
                  data-testid={`schools-package-${i}`}
                  className="p-7 rounded-2xl bg-gradient-to-br from-brand-purpleDeep/30 to-zinc-950 border border-white/10 hover:border-brand-yellow/40 transition-all"
                >
                  <CheckCircle2 className="text-brand-yellow mb-4" size={24} />
                  <h3 className="font-display text-2xl text-white tracking-wide leading-tight">{p.title}</h3>
                  <p className="mt-2 text-xs text-zinc-400 leading-relaxed">{p.desc}</p>
                  <div className="mt-6 pt-5 border-t border-white/10 flex items-baseline justify-between">
                    <span className="font-display text-2xl text-white tracking-wide">{p.price}</span>
                    <Link to="/contact" className="text-[10px] uppercase tracking-[0.25em] text-brand-yellow font-bold">Enquire →</Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="py-20 bg-gradient-to-b from-black via-zinc-950 to-black">
          <div className="max-w-5xl mx-auto px-6 sm:px-8">
            <div className="relative overflow-hidden rounded-3xl border border-brand-yellow/30 bg-gradient-to-br from-brand-purpleDeep via-black to-black p-8 sm:p-12">
              <div className="absolute -top-20 -right-20 w-[300px] h-[300px] rounded-full bg-brand-yellow/15 blur-[100px]" />
              <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-7">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow mb-3">Ready when you are</div>
                  <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-wide leading-[0.95]">
                    Tailored proposal in <span className="text-brand-yellow text-glow-yellow">48 hours</span>.
                  </h3>
                  <p className="mt-4 text-sm sm:text-base text-zinc-300 max-w-xl">
                    Tell us about your audience, dates and goals — we'll come back
                    with a tailored proposal and pricing within 48 hours.
                  </p>
                </div>
                <div className="lg:col-span-5 flex flex-col gap-3">
                  <Link
                    to="/contact"
                    data-testid="schools-request-quote-btn"
                    className="inline-flex items-center justify-between gap-3 px-6 py-4 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider hover:bg-brand-yellowHover hover:scale-[1.02] transition-all glow-yellow"
                  >
                    Request a quote <ArrowRight size={16} />
                  </Link>
                  <a
                    href={`tel:${SITE.contact.phone.replace(/\s/g, "")}`}
                    data-testid="schools-call-btn"
                    className="inline-flex items-center justify-between gap-3 px-6 py-4 rounded-full border border-white/25 bg-white/5 backdrop-blur text-white font-bold uppercase text-sm tracking-wider hover:bg-white hover:text-black hover:border-white transition-all"
                  >
                    <span className="inline-flex items-center gap-2"><Phone size={15} /> Call us</span>
                    <span className="text-xs opacity-70">{SITE.contact.phone}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section data-testid="schools-safeguarding" className="relative py-14 sm:py-16 bg-black border-t border-white/10">
          <div className="max-w-6xl mx-auto px-6 sm:px-8">
            <div className="relative overflow-hidden rounded-3xl border border-brand-yellow/25 bg-gradient-to-br from-brand-purpleDeep/30 via-zinc-950 to-black p-7 sm:p-10">
              <div className="absolute -top-20 -right-12 w-[340px] h-[340px] rounded-full bg-brand-yellow/10 blur-[100px] pointer-events-none" />
              <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
                <div className="lg:col-span-7">
                  <div className="inline-flex items-center gap-2 mb-3">
                    <ShieldCheck size={16} className="text-brand-yellow" />
                    <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.32em] text-brand-yellow">
                      Safeguarding & Policies
                    </span>
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl text-white tracking-wide leading-[1]">
                    Booking-ready paperwork for <span className="text-brand-yellow">schools, councils & festivals</span>.
                  </h3>
                  <p className="mt-3 text-sm sm:text-base text-zinc-300 max-w-2xl leading-relaxed">
                    Every USDD teacher is DBS-checked, safeguarding-trained and works to our published policies — including child protection, safer recruitment, risk assessments for outdoor classes, shows and battles, GDPR, photography consent and accessibility.
                  </p>
                  <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm text-zinc-300">
                    {[
                      "Enhanced DBS-checked staff & freelancers",
                      "Activity-specific risk assessments on request",
                      "GDPR-compliant data handling",
                      "Public liability insurance in place",
                    ].map((line) => (
                      <li key={line} className="inline-flex items-center gap-2">
                        <CheckCircle2 size={13} className="text-brand-yellow flex-shrink-0" /> {line}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="lg:col-span-5 flex flex-col gap-3">
                  <Link
                    to="/policies"
                    data-testid="schools-view-policies-btn"
                    className="inline-flex items-center justify-between gap-3 px-6 py-4 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider hover:bg-brand-yellowHover hover:scale-[1.02] transition-all glow-yellow"
                  >
                    View all policies <ArrowRight size={16} />
                  </Link>
                  <a
                    href={`mailto:info@usddnetworks.co.uk?subject=Safeguarding%20pack%20for%20${encodeURIComponent("school / organisation booking")}`}
                    data-testid="schools-request-pack-btn"
                    className="inline-flex items-center justify-between gap-3 px-6 py-4 rounded-full border border-white/20 bg-white/5 backdrop-blur text-white font-bold uppercase text-sm tracking-wider hover:bg-white hover:text-black hover:border-white transition-all"
                  >
                    <span className="inline-flex items-center gap-2"><Mail size={15} /> Request booking pack</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <RelatedLinks
          title="Related"
          items={[
            { tag: "Workshops", label: "Cultural Workshops", desc: "Public & community sessions", to: "/workshops" },
            { tag: "Online", label: "Online Courses", desc: "Stream training anywhere", to: "/online" },
            { tag: "Events", label: "Events & Battles", desc: "Showcases & competitions", to: "/events" },
          ]}
        />
      </main>
      <Footer />
    </>
  );
}
